export interface AdminMfaStatus {
  username: string;
  mfaEnabled: boolean;
  setupRequired: boolean;
  sessionMfaVerified: boolean;
  challengeId: string;
}

export interface AdminMfaSetupResponse {
  username: string;
  secret: string;
  otpauthUri: string;
  enabled: boolean;
  challengeId: string;
}

export interface AdminMfaVerifyResponse {
  verified: boolean;
  sessionMfaVerified: boolean;
  lastMfaVerifiedAt: string | null;
  failureReason: string | null;
}

const asString = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim()) return value;
  return null;
};

const unwrapJacksonEnvelope = (value: unknown): unknown => {
  if (!Array.isArray(value)) {
    return value;
  }

  if (value.length === 2 && typeof value[0] === 'string' && value[0].startsWith('java.')) {
    return unwrapJacksonEnvelope(value[1]);
  }

  return value.map((item) => unwrapJacksonEnvelope(item));
};

const extractRecord = (value: unknown): Record<string, unknown> => {
  const unwrapped = unwrapJacksonEnvelope(value);
  if (!unwrapped || typeof unwrapped !== 'object' || Array.isArray(unwrapped)) {
    return {};
  }

  const record = unwrapped as Record<string, unknown>;
  for (const key of ['data', 'result', 'payload', 'content', 'item']) {
    const nested = unwrapJacksonEnvelope(record[key]);
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return nested as Record<string, unknown>;
    }
  }

  return record;
};

export const normalizeAdminMfaStatus = (payload: unknown): AdminMfaStatus => {
  const record = extractRecord(payload);

  return {
    username: asString(record['username']) || '',
    mfaEnabled: record['mfaEnabled'] === true,
    setupRequired: record['setupRequired'] === true,
    sessionMfaVerified: record['sessionMfaVerified'] === true,
    challengeId: asString(record['challengeId']) || '',
  };
};

export const resolveActiveAdminMfaChallengeId = ({
  setupChallengeId,
  queryChallengeId,
  statusChallengeId,
}: {
  setupChallengeId?: string | null;
  queryChallengeId?: string | null;
  statusChallengeId?: string | null;
}): string =>
  asString(setupChallengeId) || asString(queryChallengeId) || asString(statusChallengeId) || '';

export const shouldOfferAdminLoginRestart = (message: string | null | undefined): boolean => {
  const normalized = asString(message)?.toLowerCase() || '';
  if (!normalized) {
    return false;
  }

  return (
    normalized.includes('status code 401') ||
    normalized.includes('status code 403') ||
    normalized.includes('권한이 없거나') ||
    normalized.includes('관리자 로그인을 다시 시작') ||
    normalized.includes('진행 중인 mfa challenge 정보를 찾지 못했습니다') ||
    normalized.includes('mfa 세션이 만료되었습니다')
  );
};

export const normalizeAdminMfaSetupResponse = (payload: unknown): AdminMfaSetupResponse => {
  const record = extractRecord(payload);

  return {
    username: asString(record['username']) || '',
    secret: asString(record['secret']) || '',
    otpauthUri: asString(record['otpauthUri']) || '',
    enabled: record['enabled'] === true,
    challengeId: asString(record['challengeId']) || '',
  };
};

export const normalizeAdminMfaVerifyResponse = (payload: unknown): AdminMfaVerifyResponse => {
  const record = extractRecord(payload);

  return {
    verified: record['verified'] === true,
    sessionMfaVerified: record['sessionMfaVerified'] === true,
    lastMfaVerifiedAt: asString(record['lastMfaVerifiedAt']),
    failureReason: asString(record['failureReason']),
  };
};
